/**
 * @fileoverview Parser for v2 managed-agent activity log messages.
 * @module shared-panels/activity-parsing
 *
 * Purpose:
 *   Convert a single `summarizationTasks.activityLog` row (a flat string like
 *   "[AssetList] Calling tool getJobMetadata") into a structured discriminated
 *   union so the timeline UI can render each row with the right affordance —
 *   animated tool pill, callout card, narrative card, or generic fallback.
 *
 * Notes:
 *   - The v2 eventBridge (convex/jobs/summarization/managed/eventBridge.ts)
 *     and per-manifest `applyOutput` calls are the source of truth for the
 *     string formats parsed here. Keep this file in sync with those writers.
 *   - v1 strings (coordinator/worker/dispatching/...) fall through to `plain`
 *     and are still styled by the keyword matcher in TimelineEntries.tsx.
 */

import { rewriteAssumedRelationBackCallout } from "@cvx/keyDates/relationBackCopy";

export type ActivityCounts = {
  inserted: number;
  updated: number;
  merged: number;
  skipped: number;
};

export type ParsedActivity =
  | { kind: "tool_call"; agent: string; toolName: string }
  | { kind: "tool_result"; agent: string; toolName: string }
  | { kind: "callout"; agent: string; body: string }
  | {
      kind: "narrative";
      agent: string;
      body: string;
      counts?: ActivityCounts;
    }
  | { kind: "session"; agent: string; body: string; tone: "info" | "error" }
  | { kind: "plain"; agent?: string; body: string };

const AGENT_PREFIX_RE = /^\[([A-Za-z0-9]+)\]\s+/;
const TOOL_CALL_RE = /^Calling tool (\S+)\s*$/;
const TOOL_RESULT_RE = /^Tool (\S+) returned\s*$/;
const CALLOUT_RE = /^CALLOUT:\s*(.+)$/s;
const COUNTS_RE =
  /\(inserted=(\d+),\s*updated=(\d+),\s*merged=(\d+),\s*skipped=(\d+)\)\s*$/;
const SESSION_INFO_RE = /^Session\s+(created|expired)/i;
const SESSION_ERROR_RE = /^Error:\s*/i;

/**
 * Parses a single activity log message into a structured ParsedActivity.
 *
 * @example
 * parseActivityMessage("[AssetList] Calling tool getJobMetadata");
 * // → { kind: "tool_call", agent: "AssetList", toolName: "getJobMetadata" }
 *
 * @example
 * parseActivityMessage(
 *   "[AssetList] Reviewed PPSR... (inserted=1, updated=0, merged=0, skipped=1)"
 * );
 * // → { kind: "narrative", agent: "AssetList", body: "...",
 * //     counts: { inserted: 1, updated: 0, merged: 0, skipped: 1 } }
 */
export function parseActivityMessage(message: string): ParsedActivity {
  const trimmed = message.trim();

  const agentMatch = trimmed.match(AGENT_PREFIX_RE);
  const agent = agentMatch?.[1];
  const body = agentMatch ? trimmed.slice(agentMatch[0].length) : trimmed;

  if (agent) {
    const toolCall = body.match(TOOL_CALL_RE);
    if (toolCall) {
      return { kind: "tool_call", agent, toolName: toolCall[1] };
    }

    const toolResult = body.match(TOOL_RESULT_RE);
    if (toolResult) {
      return { kind: "tool_result", agent, toolName: toolResult[1] };
    }

    const callout = body.match(CALLOUT_RE);
    if (callout) {
      return {
        kind: "callout",
        agent,
        body: rewriteAssumedRelationBackCallout(callout[1].trim()),
      };
    }

    if (SESSION_ERROR_RE.test(body)) {
      return {
        kind: "session",
        agent,
        body: body.replace(SESSION_ERROR_RE, "").trim(),
        tone: "error",
      };
    }

    if (SESSION_INFO_RE.test(body)) {
      return { kind: "session", agent, body, tone: "info" };
    }

    const counts = body.match(COUNTS_RE);
    if (counts) {
      const narrativeBody = body.slice(0, counts.index).trim();
      return {
        kind: "narrative",
        agent,
        body: narrativeBody.length > 0 ? narrativeBody : body,
        counts: {
          inserted: Number(counts[1]),
          updated: Number(counts[2]),
          merged: Number(counts[3]),
          skipped: Number(counts[4]),
        },
      };
    }

    // Long agent-prefixed line without counts — still treat as narrative if
    // it's substantial (>= 80 chars), otherwise fall through to plain.
    if (body.length >= 80) {
      return { kind: "narrative", agent, body };
    }

    return { kind: "plain", agent, body };
  }

  return { kind: "plain", body };
}
